import { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import { auth, db, googleProvider } from "../FirebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";

interface UserData {
  id: string;
  email: string;
  name: string;
  role: string;
  profilePic?: string;
  joinedAt: Date;
  createdAt: Date;
  hasAgreedToTerms: boolean;  // Added field to track agreement
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    name: string,
    hasAgreedToTerms: boolean
  ) => Promise<User | null>;
  signIn: (email: string, password: string) => Promise<User | null>;
  signInWithGoogle: (
    hasAgreedToTerms: boolean
  ) => Promise<User | null>;
  logout: () => Promise<void>;
}

const UserContext = createContext<AuthContextType | null>(null);

export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const createUserDocument = async (user: User, hasAgreedToTerms: boolean) => {
    const userDocRef = doc(db, "users", user.email ?? "");
    const userDoc = await getDoc(userDocRef);

    // Only create user document if it doesn't exist
    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        id: user.uid,
        email: user.email ?? "",
        name: user.displayName ?? "",
        profilePic: user.photoURL ?? "",
        role: "user",
        joinedAt: new Date(),
        createdAt: new Date(),
        hasAgreedToTerms,
      });
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    hasAgreedToTerms: boolean
  ): Promise<User | null> => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const { user } = userCredential;

      if (user) {
        await updateProfile(user, {
          displayName: name
        });

        await sendEmailVerification(user);
        
        await createUserDocument({
          ...user,
          displayName: name
        }, hasAgreedToTerms);
        
        await signOut(auth);
        toast.success("Please verify your email before logging in.");

        setUser(null);
        return user;
      }
      return null;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  };

  const signIn = async (
    email: string,
    password: string
  ): Promise<User | null> => {
    try {
      const userCredentials = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const { user } = userCredentials;

      if (!user.emailVerified) {
        await signOut(auth);
        throw new Error("Please verify your email before logging in.");
      }

      setUser(user);
      return user;
    } catch (error: any) {
      toast.error("Error signing in");
      throw error;
    }
  };

  const signInWithGoogle = async (hasAgreedToTerms: boolean): Promise<User | null> => {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const { user } = userCredential;

      if (user) {
        // Create user document and store the agreement flag
        await createUserDocument(user, hasAgreedToTerms);

        setUser(user);
        return user;
      }

      return null;
    } catch (error: any) {
      toast.error("Error signing in with Google");
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    await signOut(auth);
    setUser(null);
    setUserData(null); // Clear user data upon logout
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        if (!currentUser.emailVerified) {
          await signOut(auth);
          setUser(null);
          setLoading(false);
          toast.error("Please verify your email before signing in.");
        } else {
          setUser(currentUser);
          if (currentUser.email) {
            await fetchUserData(currentUser.email); // Fetch user data if available
          } else {
            setUserData(null); // In case of missing email
          }
          setLoading(false);
        }
      } else {
        setUser(null);
        setUserData(null);
        setLoading(false); // Clear all data on sign-out
      }
    });

    return () => {
      unsubscribe(); // Cleanup listener on component unmount
    };
  }, []);

  const fetchUserData = async (email: string): Promise<void> => {
    try {
      const userDocRef = doc(db, "users", email);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        setUserData(userDoc.data() as UserData);
      } else {
        setUserData(null);
      }
    } catch (error: any) {
      toast.error("Error fetching user data");
      console.error("Error fetching user data:", error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        userData,
        loading,
        signUp,
        signIn,
        signInWithGoogle,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(UserContext) as AuthContextType;
};
